/**
 * VulnBar — stacked horizontal severity bar used in the projects table.
 *
 * Renders the row's total vulnerability count above a thin horizontal
 * bar split into critical / high / medium / low / unassigned segments
 * proportional to their counts. Hovering the bar opens a portal'd
 * tooltip pinned to the bar's top edge that breaks down each severity
 * count + the grand total.
 *
 * Extracted verbatim (with style → CSS module migration) from the
 * 168-line `renderVulnBar` + `renderTooltip` helpers that used to live
 * inside SbomAnalysisTable.jsx — the migration onto the canonical
 * DataTable primitive needed `renderVulnBar` to stand on its own as
 * one cell's content, so the helpers became this component.
 */
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './VulnBar.module.css';

const SEVERITY_SEGMENTS = [
  { key: 'critical',   label: 'Critical',   color: '#EF4444' },
  { key: 'high',       label: 'High',       color: '#F97316' },
  { key: 'medium',     label: 'Medium',     color: '#FBBF24' },
  { key: 'low',        label: 'Low',        color: '#60A5FA' },
  { key: 'unassigned', label: 'Unassigned', color: '#9CA3AF' },
];

const VulnBar = ({
  total = 0,
  critical = 0,
  high = 0,
  medium = 0,
  low = 0,
  unassigned = 0,
}) => {
  const [tooltip, setTooltip] = useState(null); // { x, y }
  const leaveTimerRef = useRef(null);

  const safeTotal = total || 0;

  if (safeTotal === 0) {
    return <span className={styles.zero}>0</span>;
  }

  const counts = { critical, high, medium, low, unassigned };
  const visibleSegments = SEVERITY_SEGMENTS.filter((s) => counts[s.key] > 0);

  const handleMouseEnter = (e) => {
    clearTimeout(leaveTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: Math.round(rect.left + rect.width / 2),
      y: Math.round(rect.top),
    });
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => setTooltip(null), 200);
  };

  const handleTooltipMouseEnter = () => {
    clearTimeout(leaveTimerRef.current);
  };

  return (
    <>
      <div
        className={styles.wrap}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={styles.total}>{safeTotal}</span>
        <div className={styles.bar}>
          {visibleSegments.map((seg) => (
            <div
              key={seg.key}
              className={styles.segment}
              style={{
                flex: counts[seg.key] / safeTotal,
                backgroundColor: seg.color,
              }}
            />
          ))}
        </div>
      </div>

      {tooltip &&
        createPortal(
          <div
            className={styles.tooltip}
            style={{ left: tooltip.x, top: tooltip.y }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {SEVERITY_SEGMENTS.map((seg) => (
              <div key={seg.key} className={styles.tooltipRow}>
                <span className={styles.tooltipLabel} style={{ color: seg.color }}>
                  {seg.label}
                </span>
                <span className={styles.tooltipCount}>{counts[seg.key]}</span>
              </div>
            ))}
            <div className={styles.tooltipDivider}>
              <span className={styles.tooltipTotalLabel}>Total</span>
              <span className={styles.tooltipTotalValue}>{safeTotal}</span>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default VulnBar;
